import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { CreditCard, FileText, AlertTriangle, CheckCircle, Clock, Loader2, XCircle, Download } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";

interface Invoice {
  id: string; invoice_number: string; amount_excl_tax: number; tax_amount: number; amount_incl_tax: number; status: string; paid_at: string | null; period_start: string; period_end: string; created_at: string;
}

const statusColors: Record<string, string> = {
  paid: "bg-accent/10 text-accent border-accent/20",
  pending: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  failed: "bg-destructive/10 text-destructive border-destructive/20",
};

const BillingSettings = () => {
  const { subscription, subscriptionStatus, trialEndsAt, isTrialing, isTrialExpired, isActive, isPastDue, isCancelled, trialDaysLeft, loading: billingLoading, startSubscription, cancelSubscription, refreshBilling } = useBilling();
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);
  const [startingSubscription, setStartingSubscription] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  useEffect(() => {
    const fetchInvoices = async () => {
      const { data } = await supabase.from("invoices").select("*").order("created_at", { ascending: false });
      setInvoices((data as any) || []);
      setLoadingInvoices(false);
    };
    fetchInvoices();
  }, []);

  const handleStartSubscription = async () => {
    setStartingSubscription(true);
    const checkoutUrl = await startSubscription();
    if (checkoutUrl) { window.location.href = checkoutUrl; }
    else { toast.error(t("billing.start_failed")); setStartingSubscription(false); }
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    const success = await cancelSubscription();
    if (success) { toast.success(t("billing.cancel_success")); }
    else { toast.error(t("billing.cancel_failed")); }
    setCancellingSubscription(false);
  };

  const downloadInvoice = async (invoice: Invoice) => {
    setDownloadingInvoiceId(invoice.id);
    let blobUrl: string | null = null;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error("No active session");

      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invoice-download?id=${encodeURIComponent(invoice.id)}`;
      const res = await fetch(url, {
        headers: {
          apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          Authorization: `Bearer ${session.access_token}`,
          Accept: "application/pdf",
        },
      });

      if (!res.ok) throw new Error(await res.text() || "Download failed");

      const blob = await res.blob();
      if (!blob.size) throw new Error("Empty invoice PDF");

      blobUrl = URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `invoice-${invoice.invoice_number}.pdf`;
      link.rel = "noopener";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        if (blobUrl) URL.revokeObjectURL(blobUrl);
      }, 30_000);
    } catch (error) {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      console.error("Invoice download failed", error);
      toast.error(t("billing.download_failed"));
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  if (billingLoading) { return (<div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>); }

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-foreground">{t("billing.title")}</h1>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          {t("billing.subscription")}
        </h2>

        {isPastDue && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-destructive">{t("billing.payment_failed")}</p>
                <p className="text-xs text-destructive/80 mt-1">{t("billing.payment_failed_desc")}</p>
              </div>
            </div>
            <Button className="mt-3" size="sm" onClick={handleStartSubscription} disabled={startingSubscription}>
              {startingSubscription ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("billing.update_payment")}
            </Button>
          </div>
        )}

        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("billing.status")}</span>
            <Badge variant="outline" className={`text-xs capitalize ${isActive ? "bg-accent/10 text-accent border-accent/20" : isTrialing ? "bg-primary/10 text-primary border-primary/20" : isTrialExpired ? "bg-destructive/10 text-destructive border-destructive/20" : isPastDue ? "bg-destructive/10 text-destructive border-destructive/20" : "bg-muted text-muted-foreground"}`}>
              {isTrialing ? t("billing.free_trial") : isTrialExpired ? t("billing.trial_expired_label") : subscriptionStatus.replace("_", " ")}
            </Badge>
          </div>
          {isTrialing && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("billing.trial_ends")}</span>
              <span className="font-medium text-foreground">
                {trialEndsAt ? format(trialEndsAt, "MMM d, yyyy") : "—"}{" "}
                <span className="text-muted-foreground">({trialDaysLeft} {t("billing.days_left")})</span>
              </span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t("billing.plan")}</span>
            <span className="font-medium text-foreground">€35.00/month (excl. BTW)</span>
          </div>
          {subscription?.next_payment_date && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{t("billing.next_payment")}</span>
              <span className="font-medium text-foreground">{format(new Date(subscription.next_payment_date), "MMM d, yyyy")}</span>
            </div>
          )}
        </div>

        {(isTrialing || isTrialExpired) && !subscription?.mollie_subscription_id && (
          <div className="mt-4 pt-4 border-t border-border">
            {isTrialExpired && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 mb-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-destructive">{t("billing.trial_expired_title")}</p>
                    <p className="text-xs text-destructive/80 mt-1">{t("billing.trial_expired_desc")}</p>
                  </div>
                </div>
              </div>
            )}
            {!isTrialExpired && <p className="text-sm text-muted-foreground mb-3">{t("billing.start_sub_desc")}</p>}
            <Button onClick={handleStartSubscription} disabled={startingSubscription}>
              {startingSubscription ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("billing.setup_payment")}
            </Button>
          </div>
        )}

        {isActive && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-accent mb-4">
              <CheckCircle className="h-4 w-4" />
              <span>{t("billing.active_msg")}</span>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive border-destructive/30 hover:bg-destructive/5">{t("billing.cancel_sub")}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("billing.cancel_title")}</AlertDialogTitle>
                  <AlertDialogDescription>{t("billing.cancel_desc")}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("billing.keep_sub")}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleCancelSubscription} disabled={cancellingSubscription} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    {cancellingSubscription ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {t("billing.yes_cancel")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        {isCancelled && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
              <XCircle className="h-4 w-4" />
              <span>{t("billing.cancelled_msg")}</span>
            </div>
            <Button size="sm" onClick={handleStartSubscription} disabled={startingSubscription}>
              {startingSubscription ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {t("billing.reactivate")}
            </Button>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-border bg-card p-6 shadow-card">
        <h2 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          {t("billing.invoices")}
        </h2>
        {loadingInvoices ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">{t("billing.no_invoices")}</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-4 py-3">
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{inv.invoice_number}</p>
                    <p className="text-xs text-muted-foreground">{format(new Date(inv.period_start), "MMM d")} – {format(new Date(inv.period_end), "MMM d, yyyy")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-foreground">€{Number(inv.amount_incl_tax).toFixed(2)}</span>
                  <Badge variant="outline" className={`text-xs capitalize ${statusColors[inv.status] || ""}`}>{inv.status}</Badge>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={downloadingInvoiceId === inv.id} title={t("billing.download")} onClick={() => downloadInvoice(inv)}>
                    {downloadingInvoiceId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default BillingSettings;
